import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../api/client';

interface DashboardStats {
  userCounts: {
    total: number;
    minors: number;
    adults: number;
    withConsent: number;
    withoutConsent: number;
  };
  dataRetention: {
    expiredData: number;
    retentionPeriods: { [key: string]: number };
  };
  deletionRequests: {
    pending: number;
    processed: number;
    scheduled: number;
  };
  internationalCompliance: {
    countryCounts: { [key: string]: number };
  };
  recommendations: Array<{
    priority: string;
    category: string;
    message: string;
    action: string;
  }>;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await apiClient.get('/v1/admin/compliance/report');
      setStats(response.data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Dashboard</h1>
          <p className="admin-page-description">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Dashboard</h1>
          <p className="admin-page-description">Error loading dashboard data</p>
        </div>
        <div className="admin-alert danger">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Admin Dashboard</h1>
        <p className="admin-page-description">
          Overview of user management and compliance status
        </p>
      </div>

      {/* Key Metrics */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-value">{stats.userCounts.total}</div>
          <div className="admin-stat-label">Total Users</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-value">{stats.userCounts.minors}</div>
          <div className="admin-stat-label">Minor Users</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-value">{stats.userCounts.withoutConsent}</div>
          <div className="admin-stat-label">Without Consent</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-value">{stats.deletionRequests.pending}</div>
          <div className="admin-stat-label">Pending Deletions</div>
        </div>
      </div>

      {/* Recommendations */}
      {stats.recommendations.length > 0 && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">⚠️ Action Required</h2>
          </div>
          {stats.recommendations.map((rec, index) => (
            <div key={index} className={`admin-alert ${getPriorityColor(rec.priority)}`}>
              <strong>{rec.category}:</strong> {rec.message}
              <br />
              <em>Recommended Action: {rec.action}</em>
            </div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Quick Actions</h2>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Link to="/admin/users" className="admin-button">
            Manage Users
          </Link>
          <Link to="/admin/compliance" className="admin-button">
            View Compliance
          </Link>
          <Link to="/admin/data-retention" className="admin-button secondary">
            Data Retention
          </Link>
          <button 
            className="admin-button secondary"
            onClick={() => window.location.reload()}
          >
            Refresh Data
          </button>
        </div>
      </div>

      {/* User Breakdown */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">User Breakdown</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div>
            <h4>By Age Group</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li>Adults: {stats.userCounts.adults}</li>
              <li>Minors: {stats.userCounts.minors}</li>
            </ul>
          </div>
          <div>
            <h4>By Consent Status</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li>With Consent: {stats.userCounts.withConsent}</li>
              <li>Without Consent: {stats.userCounts.withoutConsent}</li>
            </ul>
          </div>
          <div>
            <h4>By Country</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {Object.entries(stats.internationalCompliance.countryCounts).map(([country, count]) => (
                <li key={country}>{country}: {count}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Data Retention Overview */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Data Retention Overview</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <h4>Retention Periods</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {Object.entries(stats.dataRetention.retentionPeriods).map(([period, count]) => (
                <li key={period}>{period}: {count} users</li>
              ))}
            </ul>
          </div>
          <div>
            <h4>Deletion Status</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li>Expired Data: {stats.dataRetention.expiredData}</li>
              <li>Pending: {stats.deletionRequests.pending}</li>
              <li>Processed: {stats.deletionRequests.processed}</li>
              <li>Scheduled: {stats.deletionRequests.scheduled}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;